import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Building2,
  CalendarRange,
  BookOpen,
  FilePen,
  IdCard,
  Trophy,
  UserCog,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Card, EmptyState, ErrorState, InlineLoading } from "@marekl/adminer/components";
import { formatRelative } from "@marekl/adminer/format";
import type { AuditLogEntry } from "../../../lib/queries/auditLogs";
import { labelAction, labelEntityType } from "../../admin/AuditLogPage/auditLabels";
import styles from "./DashboardPage.module.css";

// „Poslední aktivita" — the audit log read as a feed rather than a table.
//
// The Czech comes from AuditLogPage/auditLabels.ts, which AGENTS.md makes the
// single source for action and entity wording; this file only picks an icon
// per entity type and arranges the line.

const ENTITY_ICON: Record<string, LucideIcon> = {
  team: Building2,
  team_leadership: Building2,
  billing_info: Building2,
  person: IdCard,
  membership: IdCard,
  user: UserCog,
  session: UserCog,
  role_assignment: UserCog,
  season: CalendarRange,
  competition: Trophy,
  competition_series: Trophy,
  competition_entry: FilePen,
  ruleset: BookOpen,
  discipline: BookOpen,
  discipline_family: BookOpen,
};

function iconFor(entry: AuditLogEntry): LucideIcon {
  return (entry.entity_type && ENTITY_ICON[entry.entity_type]) || Activity;
}

export interface ActivityFeedCardProps {
  entries: AuditLogEntry[];
  isLoading?: boolean;
  error?: unknown;
}

export function ActivityFeedCard({ entries, isLoading, error }: ActivityFeedCardProps) {
  return (
    <Card>
      <Card.Header
        title="Poslední aktivita"
        actions={
          <Link to="/audit-log" className={styles.action}>
            Audit log
          </Link>
        }
      />
      <Card.Content padding="none">
        {isLoading ? (
          <div style={{ padding: "var(--adminer-space-4)" }}>
            <InlineLoading>Načítám aktivitu…</InlineLoading>
          </div>
        ) : error ? (
          <ErrorState error={error} title="Nepodařilo se načíst aktivitu." />
        ) : entries.length === 0 ? (
          <EmptyState title="Zatím žádná aktivita" />
        ) : (
          <ul className={styles.feed}>
            {entries.map((e) => {
              const Icon = iconFor(e);
              return (
                <li key={e.id} className={styles.feedRow}>
                  <span className={styles.feedIcon}>
                    <Icon size={16} aria-hidden />
                  </span>
                  <div className={styles.feedText}>
                    <span className={styles.feedSubject}>
                      {e.display_name || e.email || "Systém"}
                    </span>
                    {": "}
                    {labelAction(e.action)}
                    {e.entity_type ? ` · ${labelEntityType(e.entity_type)}` : ""}
                    <span className={styles.feedTime}>{formatRelative(e.created_at)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card.Content>
    </Card>
  );
}
